import { useState, useEffect, useRef } from 'react';
import { useAuth as useClerkAuth, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Mail, RefreshCw } from 'lucide-react';

const LS_KEY = 'dp_pending_auth';
const RESEND_COOLDOWN_SECS = 30;

type InternalFlow = 'signin' | 'signup';

interface PendingAuth {
  email: string;
  timestamp: number;
  flow: InternalFlow;
  emailAddressId?: string;
}

export default function AuthPage() {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo') ?? '/';

  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const {
    isLoaded: authLoaded,
    isSignedIn,
    signOut,
  } = useClerkAuth();
  const { isLoaded: signInLoaded, signIn, setActive: signInSetActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: signUpSetActive } = useSignUp();

  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  // internal flow tracking — drives which Clerk API we call, not shown as a toggle
  const [internalFlow, setInternalFlow] = useState<InternalFlow>('signin');
  const [pendingEmailAddressId, setPendingEmailAddressId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeCheckedRef = useRef(false);

  const isLoaded = authLoaded && signInLoaded && signUpLoaded;

  // Never show a sign-in form over an active Clerk browser session. This also
  // recovers stale /auth tabs opened before Clerk finished restoring a session.
  useEffect(() => {
    if (authLoaded && isSignedIn) {
      localStorage.removeItem(LS_KEY);
      setLocation(returnTo);
    }
  }, [authLoaded, isSignedIn, returnTo, setLocation]);

  // ── Resume in-progress Clerk attempt on page load ──────────────────────────
  useEffect(() => {
    if (!isLoaded || resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    if (signIn?.status === 'needs_first_factor') {
      const emailFactor = (signIn.supportedFirstFactors ?? []).find(
        (f: any) => f.strategy === 'email_code'
      ) as any;
      if (emailFactor) {
        setEmail(signIn.identifier ?? '');
        setPendingEmailAddressId(emailFactor.emailAddressId);
        setInternalFlow('signin');
        setStep('otp');
        return;
      }
    }

    if (
      signUp?.status === 'missing_requirements' &&
      (signUp as any).verifications?.emailAddress?.status === 'unverified'
    ) {
      setEmail(signUp.emailAddress ?? '');
      setInternalFlow('signup');
      setStep('otp');
      return;
    }

    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const pending: PendingAuth = JSON.parse(raw);
        const ageMs = Date.now() - pending.timestamp;
        if (ageMs < 10 * 60 * 1000) {
          setEmail(pending.email);
          if (pending.emailAddressId) setPendingEmailAddressId(pending.emailAddressId);
          setInternalFlow(pending.flow);
        } else {
          localStorage.removeItem(LS_KEY);
        }
      }
    } catch {
      // ignore malformed localStorage
    }
  }, [isLoaded]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetToEmail = () => {
    localStorage.removeItem(LS_KEY);
    setStep('email');
    setCode('');
    setError(null);
    setSessionConflict(false);
    setPendingEmailAddressId(null);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(0);
  };

  const resetBrowserSession = async () => {
    setLoading(true);
    setError(null);
    try {
      // This is intentionally user-initiated and resets only the Clerk session
      // visible to this browser context. It does not delete the player account.
      await signOut();
      resetToEmail();
    } catch {
      setError('Could not reset this browser session. Clear site data for doublepressgolf.com and try again.');
    } finally {
      setLoading(false);
    }
  };

  const clerkErrMsg = (err: any, fallback = 'Something went wrong. Try again.'): string => {
    return (
      err?.errors?.[0]?.longMessage ??
      err?.errors?.[0]?.message ??
      fallback
    );
  };

  const completeSessions = async (flow: InternalFlow, session: string) => {
    localStorage.removeItem(LS_KEY);
    if (flow === 'signin') {
      await signInSetActive!({ session });
    } else {
      await signUpSetActive!({ session });
    }
    setLocation(returnTo);
  };

  // ── Send code — unified: try sign-in first, auto-create if new email ───────
  const handleSendCode = async () => {
    if (!email.trim()) { setError('Enter your email address.'); return; }
    setLoading(true);
    setError(null);

    try {
      // Always attempt sign-in first
      let result;
      try {
        result = await signIn!.create({ identifier: email.trim() });
      } catch (signInErr: any) {
        const errCode = signInErr?.errors?.[0]?.code;
        if (errCode === 'form_identifier_not_found' || errCode === 'form_password_incorrect') {
          // No account yet — create one
          await trySignUp();
          return;
        }
        if (errCode === 'session_exists' || errCode === 'identifier_already_signed_in') {
          setSessionConflict(true);
          setError('This browser already has a sign-in session that DoublePress could not restore.');
          return;
        }
        throw signInErr;
      }

      if (result.status === 'complete') {
        await completeSessions('signin', result.createdSessionId!);
        return;
      }

      if (result.status === 'needs_first_factor') {
        const emailFactor = (result.supportedFirstFactors ?? []).find(
          (f: any) => f.strategy === 'email_code'
        ) as any;
        if (!emailFactor) {
          setError('Email code sign-in is not available for this account. Please contact support.');
          return;
        }
        await signIn!.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: emailFactor.emailAddressId,
        });
        setPendingEmailAddressId(emailFactor.emailAddressId);
        setInternalFlow('signin');
        localStorage.setItem(LS_KEY, JSON.stringify({
          email: email.trim(),
          timestamp: Date.now(),
          flow: 'signin',
          emailAddressId: emailFactor.emailAddressId,
        } satisfies PendingAuth));
        setStep('otp');
        startCooldown();
        return;
      }

      setError(`Unexpected sign-in state: ${result.status}. Please try again.`);
    } catch (err: any) {
      setError(clerkErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const trySignUp = async () => {
    try {
      // Provide firstName upfront so Clerk never returns missing_requirements
      // after email verification — the account will be complete immediately.
      const emailPrefix = (email.trim().split('@')[0] ?? 'Player')
        .replace(/[^a-zA-Z0-9_-]/g, '')
        .slice(0, 20) || 'player';
      await signUp!.create({ emailAddress: email.trim(), firstName: emailPrefix });
      await signUp!.prepareEmailAddressVerification({ strategy: 'email_code' });
      setInternalFlow('signup');
      localStorage.setItem(LS_KEY, JSON.stringify({
        email: email.trim(),
        timestamp: Date.now(),
        flow: 'signup',
      } satisfies PendingAuth));
      setStep('otp');
      startCooldown();
    } catch (err: any) {
      const errCode = err?.errors?.[0]?.code;
      if (errCode === 'form_identifier_exists' || errCode === 'form_identifier_exists_type') {
        // Edge case: exists in sign-up but not sign-in — retry sign-in code path
        setError('This email already has an account. Tap "Send Code" again to sign in.');
      } else {
        setError(clerkErrMsg(err));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ─────────────────────────────────────────────────────────────
  const handleVerifyCode = async () => {
    if (code.trim().length < 6) { setError('Enter the full 6-digit code.'); return; }
    setLoading(true);
    setError(null);

    try {
      if (internalFlow === 'signin') {
        const result = await signIn!.attemptFirstFactor({
          strategy: 'email_code',
          code: code.trim(),
        });
        if (result.status === 'complete') {
          await completeSessions('signin', result.createdSessionId!);
        } else {
          console.error('[auth] Unexpected sign-in status after OTP:', result.status);
          setError(`Sign-in incomplete (${result.status}). Please try again or contact support.`);
        }
      } else {
        const result = await signUp!.attemptEmailAddressVerification({ code: code.trim() });
        if (result.status === 'complete') {
          await completeSessions('signup', result.createdSessionId!);
        } else if (result.status === 'missing_requirements') {
          // Shouldn't normally happen since we pass firstName upfront in trySignUp.
          // If it does, log the missing fields and try a targeted fix.
          const missingFields: string[] = (result as any).missingFields ?? [];
          console.error('[auth] missing_requirements after OTP — missingFields:', missingFields, result);
          const emailPrefix = (email.trim().split('@')[0] ?? 'Player')
            .replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20) || 'player';
          const patch: Record<string, string> = {};
          if (missingFields.some((f: string) => f.toLowerCase().includes('first'))) patch.firstName = emailPrefix;
          if (missingFields.some((f: string) => f.toLowerCase().includes('last'))) patch.lastName = 'User';
          if (missingFields.some((f: string) => f.toLowerCase().includes('username'))) {
            patch.username = emailPrefix + Math.floor(Math.random() * 9000 + 1000);
          }
          try {
            const updated = await signUp!.update(patch as any);
            if (updated.status === 'complete') {
              await completeSessions('signup', updated.createdSessionId!);
            } else {
              setError('Sign-up could not complete. Please try again.');
            }
          } catch (patchErr) {
            console.error('[auth] missing_requirements patch failed:', patchErr);
            setError('Sign-up could not complete. Please try again.');
          }
        } else {
          console.error('[auth] Unexpected sign-up status after OTP:', result.status);
          setError(`Sign-up incomplete (${result.status}). Please try again or contact support.`);
        }
      }
    } catch (err: any) {
      const errCode = err?.errors?.[0]?.code;
      console.error('[auth] OTP verify error — code:', errCode, err);

      if (errCode === 'form_code_incorrect') {
        setError('Incorrect code — check and try again.');
      } else if (errCode === 'verification_expired' || errCode === 'form_code_expired') {
        setError('Code expired — tap Resend below to get a new one.');
      } else if (errCode === 'too_many_attempts') {
        setError('Too many attempts. Request a new code below.');
      } else if (
        errCode === 'verification_already_verified' ||
        errCode === 'session_exists' ||
        errCode === 'identifier_already_signed_in'
      ) {
        // Code already accepted or session already active — try to recover
        // by checking the current sign-in/sign-up status and completing if ready.
        try {
          if (internalFlow === 'signin' && signIn?.status === 'complete' && signIn?.createdSessionId) {
            await completeSessions('signin', signIn.createdSessionId);
            return;
          }
          if (internalFlow === 'signup' && signUp?.status === 'complete' && signUp?.createdSessionId) {
            await completeSessions('signup', signUp.createdSessionId);
            return;
          }
        } catch { /* fall through to generic */ }
        setError('Session issue — try tapping Sign In again from the home screen.');
      } else if (!err?.errors && err instanceof Error) {
        // Network-level error (TypeError, fetch failed, etc.)
        setError('Network error — check your connection and try again.');
      } else {
        setError(clerkErrMsg(err));
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend code ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError(null);

    try {
      if (internalFlow === 'signin') {
        if (!pendingEmailAddressId) { resetToEmail(); return; }
        await signIn!.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: pendingEmailAddressId,
        });
      } else {
        await signUp!.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
      localStorage.setItem(LS_KEY, JSON.stringify({
        email,
        timestamp: Date.now(),
        flow: internalFlow,
        emailAddressId: pendingEmailAddressId ?? undefined,
      } satisfies PendingAuth));
      startCooldown();
      setCode('');
      toast({ title: 'Code sent', description: `A new code was sent to ${email}` });
    } catch (err: any) {
      const errCode = err?.errors?.[0]?.code;
      if (errCode === 'session_exists' || errCode === 'identifier_already_signed_in') {
        resetToEmail();
      } else {
        setError(clerkErrMsg(err, 'Failed to resend. Try starting over.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (!isLoaded || isSignedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="auth-page">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">

        {/* Logo */}
        <div className="w-14 h-14 rounded-2xl bg-[#064E3B] flex items-center justify-center mb-4">
          <span className="text-white font-black text-lg">DP</span>
        </div>
        <h1 className="text-xl font-black text-gray-900 mb-1">DoublePress</h1>
        <p className="text-xs text-gray-400 mb-8">Every bet tracked. Every hole settled.</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 w-full max-w-sm">

          {step === 'email' ? (
            <>
              <h2 className="text-base font-bold text-gray-900 mb-1">
                Sign in or create account
              </h2>
              <p className="text-xs text-gray-400 mb-5">
                Enter your email — we'll send a 6-digit code.
              </p>

              <div className="space-y-3">
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSendCode(); }}
                  className="h-11 text-base"
                  disabled={loading}
                  data-testid="input-auth-email"
                />

                {error && (
                  <p className="text-xs text-red-600 leading-snug" data-testid="text-auth-error">
                    {error}
                  </p>
                )}

                {sessionConflict ? (
                  <Button
                    onClick={resetBrowserSession}
                    disabled={loading}
                    variant="outline"
                    className="w-full h-11 border-red-300 text-red-700 hover:bg-red-50 font-bold text-sm"
                    data-testid="button-reset-browser-session"
                  >
                    {loading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : 'Reset this browser’s sign-in'
                    }
                  </Button>
                ) : (
                  <Button
                    onClick={handleSendCode}
                    disabled={loading || !email.trim()}
                    className="w-full h-11 bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15] font-bold text-sm"
                    data-testid="button-send-code"
                  >
                    {loading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send Code
                        </>
                      )
                    }
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* OTP step */}
              <button
                onClick={resetToEmail}
                className="flex items-center gap-1.5 text-xs text-gray-500 mb-4 -ml-0.5 active:opacity-70"
                data-testid="button-back-to-email"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Use a different email
              </button>

              <h2 className="text-base font-bold text-gray-900 mb-1">Check your email</h2>
              <p className="text-sm text-gray-500 mb-5">
                Code sent to{' '}
                <span className="font-medium text-gray-800" data-testid="text-otp-email">{email}</span>
              </p>

              <div className="space-y-3">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={e => {
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) handleVerifyCode(); }}
                  className="h-14 text-2xl text-center tracking-[0.4em] font-mono"
                  disabled={loading}
                  autoFocus
                  data-testid="input-auth-otp"
                />

                {error && (
                  <p className="text-xs text-red-600 leading-snug" data-testid="text-auth-otp-error">
                    {error}
                  </p>
                )}

                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length < 6}
                  className="w-full h-11 bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15] font-bold text-sm"
                  data-testid="button-verify-code"
                >
                  {loading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : 'Verify Code'
                  }
                </Button>

                <button
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0}
                  className="w-full text-xs text-gray-500 py-2 flex items-center justify-center gap-1.5 disabled:opacity-40 active:opacity-70 transition-opacity"
                  data-testid="button-resend-code"
                >
                  <RefreshCw className="w-3 h-3" />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
